
// Purpose: Auth context provider for user, session, role, profile, and auth action state. Should not implement logic directly.

import React, { createContext, useState, useContext, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';
import { useAuthActions } from '@/hooks/useAuthActions';
import { useUserRole } from '@/hooks/useUserRole';
import { useProfile, Profile } from '@/hooks/useProfile';
import { useSessionValidation } from '@/hooks/useSessionValidation';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  userRole: 'admin' | 'client' | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  checkUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Main AuthProvider, orchestrates state and uses split-out hooks/utilities.
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { login, signup, logout, resendVerificationEmail, sendMagicLink, isLoading: authLoading } = useAuthActions();
  const { userRole, isAdmin, isRoleLoading, checkUserRole } = useUserRole(user);
  const { profile, setProfile, fetchUserProfile, updateProfile: updateProfileData } = useProfile();
  useSessionValidation();

  // Combine loading states
  const combinedIsLoading = isLoading || authLoading || isRoleLoading;

  useEffect(() => {
    if (!isSupabaseConfigured) {
      console.warn('Supabase is not configured. Auth features are disabled.');
      setIsLoading(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth state change:', event, currentSession?.user?.id);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (event === 'SIGNED_IN' && currentSession?.user) {
          setTimeout(() => fetchUserProfile(currentSession.user.id), 0);
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.log('Initial session check:', currentSession?.user?.id);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      if (currentSession?.user) {
        fetchUserProfile(currentSession.user.id);
      }
    }).finally(() => { setIsLoading(false); });

    return () => { subscription.unsubscribe(); };
  }, []);

  const updateProfile = async (data: Partial<Profile>) => {
    await updateProfileData(data, user);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAuthenticated: !!user,
        isLoading: combinedIsLoading,
        isAdmin,
        userRole,
        login,
        signup,
        logout,
        updateProfile,
        resendVerificationEmail,
        sendMagicLink,
        checkUserRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
